/**
 * swiper-slider.js
 * Custom element that initialises Swiper on multicolumn-2 sections.
 * Place this file in: assets/swiper-slider.js
 *
 * Depends on: Swiper v11 (loaded via CDN in the section file)
 */

(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Helper — load Swiper from CDN if it hasn't been loaded yet          */
  /* ------------------------------------------------------------------ */
  function loadSwiperScript(callback) {
    if (window.Swiper) {
      callback();
      return;
    }

    // Check if the script tag is already in the DOM (being fetched)
    if (document.querySelector('script[src*="swiper-bundle.min.js"]')) {
      const poll = setInterval(() => {
        if (window.Swiper) {
          clearInterval(poll);
          callback();
        }
      }, 50);
      return;
    }

    // Last resort: inject the script ourselves
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
    script.onload = callback;
    document.head.appendChild(script);
  }

  /* ------------------------------------------------------------------ */
  /* Custom Element Definition                                           */
  /* ------------------------------------------------------------------ */
  class SwiperSlider extends HTMLElement {
    connectedCallback() {
      loadSwiperScript(() => this.initSwiper());
    }

    disconnectedCallback() {
      if (this.swiperInstance) {
        this.swiperInstance.destroy(true, true);
        this.swiperInstance = null;
      }
    }

    initSwiper() {
      const swiperEl = this.querySelector('[data-swiper]');
      if (!swiperEl) return;

      // Read config from data-attributes on <swiper-slider>
      const loop       = this.dataset.loop !== 'false';
      const desktop    = parseInt(this.dataset.desktop)  || 3;
      const tablet     = parseInt(this.dataset.tablet)   || 2;
      const mobile     = parseInt(this.dataset.mobile)   || 1;
      const autoplayMs = parseInt(this.dataset.autoplay) || 0;
      const hasNav     = this.dataset.navigation === 'true';
      const hasPag     = this.dataset.pagination  === 'true';

      const config = {
        loop: loop,
        slidesPerView: mobile,
        spaceBetween: 16,

        breakpoints: {
          750: {
            slidesPerView: tablet,
            spaceBetween: 24,
          },
          990: {
            slidesPerView: desktop,
            spaceBetween: 30,
          },
        },

        // Autoplay — only when speed > 0
        ...(autoplayMs > 0 && {
          autoplay: {
            delay: autoplayMs,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          },
        }),

        // Navigation arrows
        ...(hasNav && {
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
        }),

        // Pagination dots
        ...(hasPag && {
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
          },
        }),

        // Accessibility
        a11y: {
          prevSlideMessage: 'Previous slide',
          nextSlideMessage: 'Next slide',
        },

        // Keyboard control
        keyboard: {
          enabled: true,
        },
      };

      this.swiperInstance = new Swiper(swiperEl, config);

      /* ----------------------------------------------------------------
       * Shopify Theme Editor support
       * Re-initialise when the merchant edits the section in the editor
       * -------------------------------------------------------------- */
      if (window.Shopify && Shopify.designMode) {
        document.addEventListener('shopify:section:load', (e) => {
          if (e.target.contains(this)) {
            if (this.swiperInstance) this.swiperInstance.destroy(true, true);
            this.initSwiper();
          }
        });

        document.addEventListener('shopify:block:select', (e) => {
          if (e.target.closest('swiper-slider') === this && this.swiperInstance) {
            // Find the slide index of the selected block and slide to it
            const slide = e.target;
            const slides = Array.from(this.querySelectorAll('.swiper-slide'));
            const index = slides.indexOf(slide);
            if (index >= 0) this.swiperInstance.slideTo(index);
          }
        });
      }
    }
  }

  // Define only once (guard against double-load)
  if (!customElements.get('swiper-slider')) {
    customElements.define('swiper-slider', SwiperSlider);
  }
})();